/* eslint-disable */
/* global gsap, ScrollTrigger, SplitText */

/* SplitText names each mask wrapper after the class of what it wraps, so
   `wordsClass: "word"` with `mask: "words"` produces `.word-mask`. */
const SPLIT_TEXT_WORD_MASK_CLASS = "word-mask";

class SplitTextAnimation {
  constructor() {
    this.splitInstances = [];
    this.timelines = [];
    this.init();
    console.log("SplitTextAnimation constructor");
  }

  init() {
    // Always setup resize handler to handle viewport changes
    this.setupResizeHandler();

    // Only setup animations if viewport is large enough
    if (window.innerWidth <= 1198) {
      return;
    }
    this.setupAnimations();
    /* Re-measure after any prior modules (e.g. partner-deliverables pin) updated the page */
    requestAnimationFrame(() => {
      if (typeof ScrollTrigger !== "undefined") {
        ScrollTrigger.refresh();
      }
    });
  }

  setupResizeHandler() {
    let previousViewportHeight = window.innerHeight;
    let previousViewportWidth = window.innerWidth;
    let resizeTimeout;

    const handleResize = () => {
      // Clear any existing timeout to reset the debounce timer
      if (resizeTimeout) {
        clearTimeout(resizeTimeout);
      }

      const currentViewportHeight = window.innerHeight;
      const currentViewportWidth = window.innerWidth;

      // Debounce resize events - ScrollTrigger will only refresh when resize stops
      resizeTimeout = setTimeout(() => {
        // Check if we've crossed the desktop/mobile threshold
        const wasDesktop = previousViewportWidth >= 1200;
        const isDesktop = currentViewportWidth >= 1200;

        if (wasDesktop !== isDesktop) {
          // Viewport category changed
          if (isDesktop && this.splitInstances.length === 0) {
            // Switching to desktop and animations haven't been initialized yet
            this.setupAnimations();
          } else {
            // Viewport category changed, refresh all animations
            this.refresh();
          }
        } else {
          // Same viewport category, ensure content is visible
          if (isDesktop) {
            // Desktop: settle the lines back inside their masks
            this.splitInstances.forEach((split) => {
              if (split.lines) {
                gsap.set(split.words, {
                  yPercent: 0,
                  clearProps: "transform,willChange",
                });
              }
            });
          } else {
            // Mobile: Always ensure text is visible and properly styled
            this.ensureMobileVisibility();
          }
        }

        // Update viewport height tracking
        if (currentViewportHeight !== previousViewportHeight) {
          previousViewportHeight = currentViewportHeight;
        }

        // For mobile, ensure visibility one more time after all other operations
        if (!isDesktop) {
          setTimeout(() => {
            this.ensureMobileVisibility();
          }, 50);
        }

        // Refresh ScrollTrigger ONLY when resize stops (after debounce timeout)
        // This ensures ScrollTrigger calculations are based on final viewport dimensions
        // ScrollTrigger.refresh();

        // Refresh other components that depend on viewport changes
        if (typeof window.blogSlider === "function") {
          window.blogSlider();
        }

        // Update previous width for next comparison
        previousViewportWidth = currentViewportWidth;
      }, 100); // Increased debounce time for better stability
    };

    window.addEventListener("resize", handleResize);
    this.resizeHandler = handleResize;
  }

  setupAnimations() {
    gsap.registerPlugin(ScrollTrigger);
    const splitTextElements = document.querySelectorAll(
      ".split-text__wrapper h2, .split-text__wrapper h1",
    );

    splitTextElements.forEach((element) => {
      this.createSplitAnimation(element);
    });
  }

  normalizeManualLineBreaksForSplitText(element) {
    if (!element?.innerHTML) return;
    const html = element.innerHTML;
    if (!/<br\s*\/?>/i.test(html)) return;
    const parts = html
      .split(/<br\s*\/?>/i)
      .map((part) => part.trim())
      .filter((part) => part.length > 0);
    element.innerHTML = parts
      .map((part) => `<span class="split-text__manual-line">${part}</span>`)
      .join("");
  }

  /**
   * Split each `.split-text__manual-line` separately — SplitText often fails to
   * populate chars/lines when splitting an h2 that only contains wrapper spans.
   */
  createSplitTextInstance(element) {
    this.normalizeManualLineBreaksForSplitText(element);

    const splitTextConfig = {
      type: "lines,words,chars",
      mask: "words",
      linesClass: "line",
      wordsClass: "word",
      charsClass: "char",
      reduceWhiteSpace: false,
      prepareText: (text) => text.replace(/<br\s*\/?>/gi, "\n"),
    };

    const manualLines = [
      ...element.querySelectorAll(".split-text__manual-line"),
    ];

    if (manualLines.length > 0) {
      const splits = manualLines.map(
        (lineEl) => new SplitText(lineEl, splitTextConfig),
      );
      splits.forEach((s) => this.splitInstances.push(s));
      return {
        chars: splits.flatMap((s) => [...(s.chars || [])]),
        words: splits.flatMap((s) => [...(s.words || [])]),
        lines: splits.flatMap((s) => [...(s.lines || [])]),
      };
    }

    const split = new SplitText(element, splitTextConfig);
    this.splitInstances.push(split);
    return split;
  }

  createSplitAnimation(element) {
    const split = this.createSplitTextInstance(element);

    // Get colors from data attributes or CSS custom properties
    const wrapper = element.closest(".split-text__wrapper");
    const section = element.closest(".split-text");

    // Ensure parent split-text element is visible
    if (section) {
      gsap.set(section, {
        opacity: 1,
        visibility: "visible",
      });
    }

    let initialColor =
      this.getColor(section, wrapper, "initial") || "var(--black-45)";
    let targetColor =
      this.getColor(section, wrapper, "target") || "var(--black)";

    // Convert CSS variables to computed values for proper interpolation
    initialColor = this.resolveColor(initialColor, element);
    targetColor = this.resolveColor(targetColor, element);

    // Optional: `data-split-text-color-stagger="reverse"` — last chars hit target first
    // (e.g. estimator banner: keep top lines white longer while lower lines move toward target)
    const colorStaggerFrom =
      section?.dataset.splitTextColorStagger === "reverse" ? "end" : "start";

    // Parse custom trigger start position from data attribute
    let triggerStart = "top 90%"; // Default trigger start
    if (section && section.dataset.splitTextStart !== undefined) {
      const startPercent = parseFloat(section.dataset.splitTextStart);
      if (!isNaN(startPercent)) {
        triggerStart = `top ${startPercent}%`;
      }
    }
    // Check if we're on desktop (>= 1200px) for animations
    const isDesktop = window.innerWidth >= 1200;
    if (isDesktop) {
      // Desktop: Apply clip animation with better visibility handling
      gsap.set(split.words, {
        opacity: 1,
        yPercent: 100,
        force3D: true,
        willChange: "transform",
        visibility: "visible", // Ensure elements are visible
      });

      gsap.set(split.chars, {
        color: initialColor,
        force3D: true,
        willChange: "color",
        visibility: "visible", // Ensure characters are visible
      });

      // Check if element is already in view (for hero sections at top of page)
      const rect = wrapper.getBoundingClientRect();
      const windowHeight = window.innerHeight;
      // Parse trigger percent from triggerStart (e.g., "top 10%" -> 0.1)
      const triggerPercent =
        parseFloat(triggerStart.split(" ")[1]) / 100 || 0.9;
      const triggerPoint = windowHeight * triggerPercent;
      const isAlreadyInView = rect.top < triggerPoint;

      // Check if element is already past the color animation trigger point
      const colorTriggerStart = "top 50%";
      const colorTriggerPercent = 0.5; // 50%
      const colorTriggerPoint = windowHeight * colorTriggerPercent;
      const isAlreadyInViewForColor = rect.top < colorTriggerPoint;
      const colorEnd = isAlreadyInViewForColor ? "bottom 10%" : "bottom 60%";

      // If element is already past color trigger point, ensure initial color is set and disable scrub
      // to prevent ScrollTrigger from applying colors instantly
      if (isAlreadyInViewForColor) {
        // Set initial color for all characters immediately to prevent any color application
        gsap.set(split.chars, { color: initialColor });
      }

      // Create color timeline first so it's accessible in revealTl's onComplete
      const colorTl = gsap.timeline({
        scrollTrigger: {
          trigger: wrapper,
          start: colorTriggerStart,
          end: colorEnd,
          scrub: 0.8, // Ties animation progress to scroll position (allows reverse)
          once: false, // Allow reverse scrolling
          onUpdate: (self) => {
            element.style.setProperty("--scroll-progress", self.progress);
          },
          onRefresh: function () {
            // If element is already past trigger, set color immediately
            const rect = wrapper.getBoundingClientRect();
            const triggerPercent =
              parseFloat(triggerStart.split(" ")[1]) / 100 || 0.9;
            const triggerPoint = window.innerHeight * triggerPercent;
            if (rect.top < triggerPoint) {
              gsap.set(split.chars, {
                // color: targetColor,
              });
            }
          },
        },
      });

      if (!isAlreadyInViewForColor) {
        colorTl.fromTo(
          split.chars,
          {
            color: initialColor,
          },
          {
            color: targetColor,
            stagger: { each: 0.8, from: colorStaggerFrom },
            ease: "power2.inOut",
            duration: 1,
            immediateRender: false,
            overwrite: "auto",
            force3D: true,
          },
        );
      }

      const revealTl = gsap.timeline({
        scrollTrigger: {
          trigger: wrapper,
          start: triggerStart,
          toggleActions: "play none none none",
          once: true,
          onEnter: function () {
            // Ensure animation plays when entering trigger zone
            if (!revealTl.isActive() && revealTl.progress() === 0) {
              revealTl.play();
            }
          },
          onRefresh: function () {
            // Check again on refresh if element is already past trigger
            const rect = wrapper.getBoundingClientRect();
            const triggerPercent =
              parseFloat(triggerStart.split(" ")[1]) / 100 || 0.9;
            const triggerPoint = window.innerHeight * triggerPercent;
            // If element is already past trigger point, play animation immediately
            if (
              rect.top < triggerPoint &&
              !revealTl.isActive() &&
              revealTl.progress() === 0
            ) {
              revealTl.play();
            }
          },
        },
      });

      // Desktop: Full animation with clip path and better visibility
      revealTl.to(split.words, {
        yPercent: 0,
        stagger: { each: 0.055, from: "start" },
        ease: "expo.out",
        duration: 1.1,
        onComplete: () => {
          // Ensure all text is visible after animation
          gsap.set(split.words, {
            visibility: "visible",
            opacity: 1,
            clearProps: "transform,willChange",
          });
          // SplitText's word masks are `overflow: clip` and only as tall as the
          // line box, which at this display size is shorter than the font —
          // so a settled descender (the tail of a "g") stays cropped. The mask
          // has done its job by the time the words have landed; release it.
          const wordMasks = split.words
            .map((word) => word.parentElement)
            .filter(
              (parent) =>
                parent && parent.classList.contains(SPLIT_TEXT_WORD_MASK_CLASS),
            );
          if (wordMasks.length > 0) {
            gsap.set(wordMasks, { overflow: "visible" });
          }
          // Ensure parent split-text element is visible
          if (section) {
            gsap.set(section, {
              opacity: 1,
              visibility: "visible",
            });
          }
          // Ensure br tags are properly displayed as line breaks
          this.preserveBrTags(element);

          // If element is already past color trigger point on initial load, animate color transition smoothly
          if (isAlreadyInViewForColor && colorTl.scrollTrigger) {
            gsap.set(split.chars, { color: initialColor });

            let scrollProgress = 0;
            if (colorTl.scrollTrigger) {
              ScrollTrigger.refresh();
              scrollProgress = colorTl.scrollTrigger.progress || 0;
              colorTl.scrollTrigger.disable();
            }
            if (scrollProgress === 0) {
              // Calculate scroll progress manually based on element position
              const currentRect = wrapper.getBoundingClientRect();
              const windowHeight = window.innerHeight;
              const triggerStartPoint = windowHeight * 0.5; // top 50%

              // Calculate where the end point would be based on ScrollTrigger logic
              const elementHeight = currentRect.height;
              const triggerEndPoint = isAlreadyInViewForColor
                ? windowHeight * 0.1 - elementHeight // bottom 10%
                : windowHeight * 0.6 - elementHeight; // bottom 60%

              // Calculate progress: 0 when element top is at trigger start, 1 when element top is at trigger end
              const elementTop = currentRect.top;
              const scrollRange = triggerStartPoint - triggerEndPoint;
              if (scrollRange > 0) {
                scrollProgress = Math.max(
                  0,
                  Math.min(1, (triggerStartPoint - elementTop) / scrollRange),
                );
              }
            }

            if (scrollProgress > 0) {
              if (colorTl.scrollTrigger) {
                colorTl.scrollTrigger.enable();
                colorTl.fromTo(
                  split.chars,
                  {
                    color: initialColor,
                  },
                  {
                    color: targetColor,
                    stagger: { each: 0.8, from: colorStaggerFrom },
                    ease: "power2.inOut",
                    duration: 1,
                    immediateRender: false,
                    overwrite: "auto",
                    force3D: true,
                  },
                );
              }
            }
          }
        },
      });

      if (isAlreadyInView) {
        revealTl.play();
      } else {
        if (revealTl.scrollTrigger) {
          const progress = revealTl.scrollTrigger.progress;
          // If ScrollTrigger shows we're already past the trigger point
          if (
            progress > 0 &&
            !revealTl.isActive() &&
            revealTl.progress() === 0
          ) {
            revealTl.play();
          }
        }
      }

      this.timelines.push(revealTl, colorTl);
    } else {
      // Mobile: No animations, just set final state immediately
      // Ensure parent split-text element is visible
      if (section) {
        gsap.set(section, {
          opacity: 1,
          visibility: "visible",
        });
      }

      gsap.set(split.lines, {
        opacity: 1,
        y: 0,
        transformOrigin: "center bottom",
      });

      gsap.set(split.chars, {
        color: targetColor, // Set to final color immediately
        force3D: true,
        willChange: "color",
      });

      // Ensure br tags are properly displayed on mobile
      this.preserveBrTags(element);

      // No timelines for mobile - text appears immediately
    }
  }

  getColor(section, wrapper, type) {
    // Priority order: data attributes -> CSS custom properties -> null

    // 1. Check data attributes on wrapper
    let color =
      wrapper?.dataset[type === "initial" ? "initialColor" : "targetColor"];
    if (color) return color;

    // 2. Check data attributes on section
    color =
      section?.dataset[type === "initial" ? "initialColor" : "targetColor"];
    if (color) return color;

    // 3. Check CSS custom properties on section
    if (section) {
      const cssVar =
        type === "initial"
          ? "--split-text-initial-color"
          : "--split-text-target-color";
      const computedColor = getComputedStyle(section)
        .getPropertyValue(cssVar)
        .trim();
      if (computedColor) return computedColor;
    }

    // 4. Check CSS custom properties on wrapper
    if (wrapper) {
      const cssVar =
        type === "initial"
          ? "--split-text-initial-color"
          : "--split-text-target-color";
      const computedColor = getComputedStyle(wrapper)
        .getPropertyValue(cssVar)
        .trim();
      if (computedColor) return computedColor;
    }

    return null;
  }

  resolveColor(color, element) {
    // If it's a CSS variable, resolve it to actual color value
    if (color && color.startsWith("var(")) {
      // Create a temporary element to get computed color
      const tempElement = document.createElement("div");
      tempElement.style.color = color;
      tempElement.style.position = "absolute";
      tempElement.style.visibility = "hidden";
      element.parentNode.appendChild(tempElement);

      const computedColor = getComputedStyle(tempElement).color;
      element.parentNode.removeChild(tempElement);

      return computedColor || color;
    }

    return color;
  }

  destroy() {
    this.timelines.forEach((tl) => tl.kill());
    this.timelines = [];
    this.splitInstances.forEach((split) => split.revert());
    this.splitInstances = [];
    if (this.resizeHandler) {
      window.removeEventListener("resize", this.resizeHandler);
      this.resizeHandler = null;
    }
    // ScrollTrigger.refresh();
  }

  preserveBrTags(element) {
    // Ensure br tags are properly displayed as line breaks
    // This method is called after animations to restore proper line breaks
    const brElements = element.querySelectorAll("br");
    brElements.forEach((br) => {
      br.style.display = "block";
      br.style.height = "0";
      br.style.lineHeight = "0";
      br.style.margin = "0";
      br.style.padding = "0";
    });
  }

  ensureMobileVisibility() {
    // Ensure all split text elements are visible on mobile
    // First, ensure all parent split-text elements are visible
    const allSplitTextElements = document.querySelectorAll(".wrg.split-text");
    allSplitTextElements.forEach((section) => {
      gsap.set(section, {
        opacity: 1,
        visibility: "visible",
      });
    });

    this.splitInstances.forEach((split) => {
      // Ensure lines are visible
      if (split.lines && split.lines.length > 0) {
        gsap.set(split.lines, {
          clearProps: "transform,opacity,willChange",
          opacity: 1,
          y: 0,
          visibility: "visible",
          display: "block",
          overflow: "visible",
        });
      }

      // Ensure words are visible
      if (split.words && split.words.length > 0) {
        gsap.set(split.words, {
          clearProps: "transform,opacity,willChange",
          opacity: 1,
          y: 0,
          visibility: "visible",
          display: "inline",
          overflow: "visible",
        });
      }

      // Set characters to final color and ensure visibility
      if (split.chars && split.chars.length > 0) {
        // Find the original element with better fallback
        const element =
          split.lines?.[0]?.parentElement?.closest("h1, h2") ||
          split.words?.[0]?.parentElement?.closest("h1, h2") ||
          split.chars?.[0]?.parentElement?.closest("h1, h2") ||
          split.lines?.[0]?.closest("h1, h2") ||
          split.words?.[0]?.closest("h1, h2") ||
          split.chars?.[0]?.closest("h1, h2");

        if (element) {
          const wrapper = element.closest(".split-text__wrapper");
          const section = element.closest(".split-text");
          const targetColor =
            this.getColor(section, wrapper, "target") || "var(--black)";
          const resolvedColor = this.resolveColor(targetColor, element);
          gsap.set(split.chars, {
            color: resolvedColor,
            clearProps: "transform,opacity,willChange",
            opacity: 1,
            y: 0,
            visibility: "visible",
            display: "inline",
            overflow: "visible",
          });

          // Ensure br tags are properly displayed on mobile
          this.preserveBrTags(element);
        } else {
          // Fallback if element not found
          gsap.set(split.chars, {
            clearProps: "transform,opacity,willChange",
            opacity: 1,
            y: 0,
            visibility: "visible",
            display: "inline",
            overflow: "visible",
            color: "var(--black)", // Default color
          });
        }
      }
    });
  }

  refresh() {
    // Store current scroll progress before refresh
    const currentProgress = {};
    this.timelines.forEach((tl, index) => {
      if (tl.scrollTrigger) {
        currentProgress[index] = tl.scrollTrigger.progress;
      }
    });

    this.destroy();
    this.setupAnimations();

    // For mobile, ensure all text is immediately visible
    const isMobile = window.innerWidth < 1200;
    if (isMobile) {
      setTimeout(() => {
        this.ensureMobileVisibility();
      }, 10);
    } else {
      // Restore scroll progress for desktop if needed
      setTimeout(() => {
        this.timelines.forEach((tl, index) => {
          if (tl.scrollTrigger && currentProgress[index] !== undefined) {
            tl.scrollTrigger.progress = currentProgress[index];
          }
        });
      }, 50);
    }
  }
}

new SplitTextAnimation();
